import { supabase } from "../supabase/SupabaseClient";
import { clave } from "../shared/unidad";
import { estaAbierto } from "../shared/ficha";

/* Los datos de la ficha de una unidad.
 *
 * ── UNA LLAMADA POR BLOQUE, Y NO UN RPC QUE TRAIGA TODO ───────────────────
 *
 * Es la decisión de diseño de este archivo y tiene dos motivos.
 *
 * EL PRIMERO ES QUE UN BLOQUE NO PUEDE LLEVARSE PUESTOS A LOS OTROS. Con una
 * sola consulta, un `taller_tickets` lento deja la ficha entera en blanco —
 * incluido el RTO, que es lo que la persona vino a mirar. Es exactamente la
 * regla que ya está escrita en el RTO de furgones: *«la ubicación no puede
 * tumbar el veredicto»*, y por eso va en su propio `try`.
 *
 * EL SEGUNDO ES EL PERMISO. Cada bloque cuelga del permiso de SU pantalla, así
 * que el que no puede abrir la ticketera **no pide** los tickets, en vez de
 * pedirlos y no dibujarlos. No es la pared —lo que restringe de verdad es la
 * base— pero no hay ninguna razón para traer por la red algo que no se va a
 * mostrar.
 *
 * A cambio son cinco idas en vez de una. Van en paralelo y ninguna recorre
 * tablas grandes salvo `ultimo_cruce`, que está medido en 25 ms porque filtra
 * adentro de la función y no sobre una vista.
 *
 * ── Y NINGUNA DEVUELVE UN VALOR INVENTADO CUANDO NO HAY DATO ──────────────
 *
 * `null` es «no hay», y una excepción es «no pude». La pantalla los dibuja
 * distinto porque mandan a hacer cosas opuestas: el hueco se carga, la falla
 * se reintenta. Devolver `null` en los dos casos es lo que hace que alguien
 * salga a corregir un dato que estaba bien.
 */

/* ── Taller: la estadía y los tickets ─────────────────────────────────────
 *
 * Las dos tablas van por la CLAVE de la unidad (`774` / `F774`) y no por el
 * número pelado. Es lo que evita que la ficha del furgón 774 muestre las
 * reparaciones del tractor homónimo — son 430 números que existen en las dos
 * tablas, así que no es un caso raro.
 */
export async function tallerDe(tipo, interno) {
  const k = clave(tipo, interno);
  if (!k) return { estadia: null, tickets: [] };

  const [estadias, tickets] = await Promise.all([
    supabase
      .from("taller_estadias")
      .select("id, interno, entrada, salida, motivo, nota, lugar")
      .eq("interno", k)
      .is("salida", null)
      .order("entrada", { ascending: false })
      .limit(1),
    supabase
      .from("taller_tickets")
      .select("id, interno, taller, lugar, tarea, mecanico, prioridad, estado, creado, empezado, vence")
      .eq("interno", k)
      .order("creado", { ascending: false })
      .limit(20),
  ]);

  if (estadias.error) throw estadias.error;
  if (tickets.error) throw tickets.error;

  return {
    estadia: estadias.data?.[0] || null,
    /* El filtro de abiertos se hace acá y no en el `select`: la regla de qué
     * cuenta como abierto vive en `shared/ficha`, y escrita también como un
     * `.in()` en la consulta serían dos definiciones que un día difieren. */
    tickets: (tickets.data || []).filter(estaAbierto),
  };
}

/* ── El historial: qué le hicieron, de las dos fuentes ────────────────────
 *
 * UNE, NO COPIA. `historial_unidad()` mezcla `taller_tickets` con
 * `puerto_trabajos` y cada fila conserva su `fuente`. Son tabla propia a
 * propósito —la ticketera mide SLA y producción, y los trabajos de puerto
 * moverían esos números sin que nadie lo decidiera— pero para el que abre la
 * ficha del 774 la pregunta es una sola: «¿qué le hicieron?».
 *
 * ES UNA FUNCIÓN Y NO DOS CONSULTAS. Con dos habría que ordenarlas y recortarlas
 * en el navegador, y el «últimos 40» saldría mal: cuarenta de cada tabla no son
 * los cuarenta más nuevos. Es la misma razón por la que `ubicaciones_ultimas`
 * filtra adentro.
 */
export async function historialDe(tipo, interno) {
  const k = clave(tipo, interno);
  if (!k) return [];
  const { data, error } = await supabase.rpc("historial_unidad", {
    p_interno: k,
    p_limite: 40,
  });
  if (error) throw error;
  return data || [];
}

/* ── Los repuestos colocados: de CantApp y de la ticketera ────────────────
 *
 * Igna, 22/10: la CantApp vieja se retira y la ficha tiene que decir cada
 * pieza que se le puso a la unidad. `repuestos_unidad()` une los eventos
 * viejos (`eventos.repuestos`) con `taller_ticket_repuestos`, ya ordenados
 * —el más nuevo primero— y por la CLAVE, como el historial: es una función y
 * no dos consultas por la misma razón (los «últimos N» de dos tablas no son
 * los N más nuevos), y la regla de «el 10 no es el 109» vive en un solo lugar.
 */
export async function repuestosDe(tipo, interno) {
  const k = clave(tipo, interno);
  if (!k) return [];
  const { data, error } = await supabase.rpc("repuestos_unidad", {
    p_interno: k,
    p_limite: 500,
  });
  if (error) throw error;
  return data || [];
}

/* ── AdBlue ────────────────────────────────────────────────────────────────
 *
 * Sólo tractores, y no es un olvido: un furgón no tiene motor, así que no hay
 * urea que consumir. Mostrar el bloque vacío en su ficha haría creer que a ese
 * acoplado le falta un dato.
 *
 * `null` cuando no hay fila — que son las 86 unidades que todavía no
 * reportaron. La pantalla lo dice como «no sabemos» y nunca como «no usa».
 */
export async function adblueDe(interno) {
  const k = clave("tractor", interno);
  if (!k) return null;
  const { data, error } = await supabase
    .from("adblue_padron")
    .select("clave, tiene, fuente, detalle, visto_ultimo")
    .eq("clave", k)
    .limit(1);
  if (error) throw error;
  return data?.[0] || null;
}

/* ── Según Scania: la foto de las 08:00 ────────────────────────────────────
 *
 * Igna, 09/10: «no tenemos actualizados los datos automáticamente de los
 * tractores desde SCANIA […] Todos los días a las 08:00», y enseguida: «Los
 * km se guardan los dos, no pises lo manual». FlotaBot lee My Scania a esa
 * hora y lo guarda en `scania_lectura_diaria`, APARTE del padrón; la vista
 * `scania_ultima` ya elige la lectura que vale para cada interno (el equipo
 * activo, la fuente fuerte, la señal más nueva). Acá no se vuelve a elegir: se
 * pide la fila de la vista y nada más, para que la ficha y el bot no puedan
 * contestar distinto sobre el mismo camión.
 *
 * SÓLO TRACTORES, como el AdBlue: un furgón no tiene Communicator.
 *
 * TRES RESPUESTAS Y NO DOS:
 *
 *   { vista: false }               la migración todavía no está aplicada. No
 *                                  es «no pude» (no hay nada que reintentar)
 *                                  ni «no hay» (no se preguntó): la tarjeta
 *                                  no aparece y la ficha sigue andando. El
 *                                  Hub se publica por su lado y puede llegar
 *                                  antes que la base.
 *   { vista: true, lectura: null } Scania no ve esta unidad (son ~200 de los
 *                                  384 Scania activos, sin Communicator).
 *   { vista: true, lectura: {…} }  lo que dijo Scania, con su fecha.
 *
 * Cualquier otro error SE LANZA: es «no pude consultar», y se dice.
 *
 * Y AL LADO, LO ANOTADO A MANO. El padrón no tiene km (`tractores` no tiene
 * esa columna); el único odómetro que hoy escribe una persona es el del vale
 * de urea, que el bot lee de la foto. Con `conVale` se trae el último de los
 * últimos 60 días, para mostrarlo AL LADO del de Scania —«se guardan los
 * dos»—, nunca en su lugar. Va en paralelo y su falla no tumba la tarjeta: es
 * un renglón, y dice que no se pudo. `conVale` es el permiso de leer las
 * cargas de urea (`ver.combustible` o `ver.urea`, lo mismo que pide su RLS):
 * sin él la base devolvería cero filas, y eso se leería como «nadie anotó
 * nada», que sería falso.
 */
export const COLUMNAS_SCANIA =
  "fecha, equipo, interno, interno_fuente, chasis, vin, equipo_telematico, " +
  "dado_de_baja, odometro_km, horas_motor, ultima_senal, en_rfms, norma, " +
  "modelo_rfms, service_km, service_objetivo_km, service_fecha, " +
  "service_fuente, capturado_en";

/* ¿La vista todavía no existe? PostgREST 12 contesta 404 con PGRST205 («Could
 * not find the table … in the schema cache»); los anteriores, 42P01
 * («relation … does not exist»). Se mira el CÓDIGO, o el 404 con el nombre de
 * la vista en el mensaje — y no un «does not exist» suelto: una COLUMNA que
 * falta (42703) también lo dice, y eso sí es un error que se tiene que ver. */
export const faltaVistaScania = (error, status) =>
  Boolean(error) &&
  (["PGRST205", "42P01"].includes(error.code) ||
    (status === 404 &&
      /scania_ultima|scania_lectura_diaria/.test(String(error.message || ""))));

const DIAS_DEL_VALE = 60;

export async function scaniaDe(interno, { conVale = false, ahora = new Date() } = {}) {
  const k = clave("tractor", interno);
  if (!k) return { vista: true, lectura: null, vale: null, valeFallo: false };

  const desde = new Date(ahora.getTime() - DIAS_DEL_VALE * 86400000);
  /* El vale, en su propio `try` y envolviendo la LLAMADA (no sólo la promesa):
   * lo que revienta antes de devolverla tampoco puede llevarse la tarjeta. */
  const pedirVale = async () => {
    try {
      return await supabase
        .from("urea_cargas")
        .select("km, fecha")
        .eq("interno", k)
        .not("km", "is", null)
        /* Un vale fechado en 2029 es un dedazo, no un dato nuevo. */
        .lte("fecha", ahora.toISOString())
        .gte("fecha", desde.toISOString())
        /* Los que alguien ya revisó y marcó mal leídos, afuera. */
        .or("lectura_estado.is.null,lectura_estado.not.in.(mal,no_leyo)")
        .order("fecha", { ascending: false })
        .limit(1);
    } catch (error) {
      return { error };
    }
  };
  const [lect, vale] = await Promise.all([
    supabase
      .from("scania_ultima")
      .select(COLUMNAS_SCANIA)
      .eq("interno", k)
      .limit(1),
    conVale ? pedirVale() : null,
  ]);

  if (lect.error) {
    if (faltaVistaScania(lect.error, lect.status)) return { vista: false };
    throw lect.error;
  }

  return {
    vista: true,
    lectura: lect.data?.[0] || null,
    vale: vale && !vale.error ? vale.data?.[0] || null : null,
    valeFallo: Boolean(vale?.error),
  };
}

/* ── El último cruce de portería ──────────────────────────────────────────
 *
 * La regla vive en la base (`ultimo_cruce`) y no acá, por lo mismo que
 * `ubicaciones_choferes`: FlotaBot contesta la misma pregunta por WhatsApp, y
 * con una copia propia de cada lado el día que difieran habría dos respuestas
 * para el mismo camión y ninguna forma de saber cuál creer.
 *
 * Cero filas es «nunca cruzó», y eso NO es un error.
 */
export async function ultimoCruceDe(tipo, interno) {
  if (!tipo || !interno) return null;
  const { data, error } = await supabase.rpc("ultimo_cruce", {
    p_tipo: tipo,
    p_interno: String(interno),
  });
  if (error) throw error;
  return data?.[0] || null;
}

/* ── Los últimos viajes de la unidad ──────────────────────────────────────
 *
 * `movimientos` tiene RLS sin políticas —la lee FlotaBot, no el front—, así que
 * va por el RPC `viajes_de_unidad`, acotado a esta unidad y a los últimos ocho.
 * Es la misma regla que `ultimo_cruce`: la consulta vive en la base.
 *
 * Devuelve `[]` cuando la unidad no tiene viajes cargados (es un hueco, no una
 * falla), y una excepción si no se pudo consultar. */
export async function viajesDeUnidad(tipo, interno) {
  if (!tipo || !interno) return [];
  const { data, error } = await supabase.rpc("viajes_de_unidad", {
    p_tipo: tipo,
    p_interno: String(interno),
  });
  if (error) throw error;
  return data || [];
}
